import { useCallback, useEffect, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { PageFrame } from 'shared/components/page-frame'
import { ScanInput } from 'shared/components/scan-input'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { ToneBanner, type Tone } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'

/**
 * The operator's inventory scan at their own Point (ADR-0045, Release 2 ticket 54). The
 * section is offered only while the Point has an open inventory. The operator scans the
 * parcels on the shelf; each answer is the server's, in its colour:
 *
 *  - green «На полку» — the parcel was on the partner's list: it is now TezGo's Parcel
 *    on the shelf (the code, the Recipient, the amount, «оплачено у партнёра»);
 *  - yellow «Уже отсканирована», «Уже посылка TezGo — не из инвентаризации»: nothing
 *    written;
 *  - red «Нет в списке — отложите»: an extra, recorded once against the inventory;
 *  - red refusals: the inventory closed, the Point not on TezGo.
 *
 * The hub's look: the figures on top («Отсканировано X из N», what is left, the
 * extras — the server's after every answer), the scan field in focus, the answer under
 * it (`ToneBanner`), then what was scanned on this visit — kept on the screen only.
 * Partners are counted separately: with two open inventories the operator picks whose
 * shelf. The calls are injected.
 */
export type OpenInventories = RouterOutputs['inventories']['AtMyPoint']
export type InventoryScanRequest = RouterInputs['inventories']['Scan']
export type InventoryScanAnswer = RouterOutputs['inventories']['Scan']
type Counts = OpenInventories[number]['counts']

export interface PointInventoryScanProps {
	list?: () => Promise<OpenInventories>
	scan?: (input: InventoryScanRequest) => Promise<InventoryScanAnswer>
}

const listViaTrpc = () => trpcClient.inventories.AtMyPoint.query()
const scanViaTrpc = (input: InventoryScanRequest) => trpcClient.inventories.Scan.mutate(input)

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))
const amount = (n: number) => n.toLocaleString('ru-RU')

/** One scan of this visit, as the screen remembers it. */
type Scanned = { id: number; code: string; answer: InventoryScanAnswer; title: string; tone: Tone }

export function PointInventoryScan({ list = listViaTrpc, scan = scanViaTrpc }: PointInventoryScanProps) {
	const { t } = useTranslate()
	const words = useAnswerWords()
	const [open, setOpen] = useState<OpenInventories | null>(null)
	const [chosen, setChosen] = useState<string | null>(null)
	const [answer, setAnswer] = useState<InventoryScanAnswer | null>(null)
	const [counts, setCounts] = useState<Record<string, Counts>>({})
	const [scanned, setScanned] = useState<Record<string, Scanned[]>>({})
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	// Bumped after every answer, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)
	const ids = useRef(0)

	const load = useCallback(async () => {
		const rows = await list()
		setOpen(rows)
		setCounts(Object.fromEntries(rows.map((r) => [r.inventoryId, r.counts])))
		setChosen((current) =>
			current && rows.some((r) => r.inventoryId === current) ? current : rows.length === 1 ? rows[0]!.inventoryId : null,
		)
	}, [list])

	useEffect(() => {
		load().catch((e) => setError(t('inventoryScan.failed', { message: failure(e) })))
	}, [load, t])

	const onScan = async (code: string) => {
		if (!chosen || busy) return
		setBusy(true)
		setError(null)
		try {
			const result = await scan({ inventoryId: chosen, code })
			setAnswer(result)
			if (result.outcome !== 'refused') setCounts((c) => ({ ...c, [chosen]: result.counts }))
			const { title, tone } = words(result)
			const id = ids.current++
			setScanned((s) => ({ ...s, [chosen]: [{ id, code, answer: result, title, tone }, ...(s[chosen] ?? [])].slice(0, 200) }))
		} catch (e) {
			setAnswer(null)
			setError(t('inventoryScan.failed', { message: failure(e) }))
		} finally {
			setBusy(false)
			setScanRound((n) => n + 1)
		}
	}

	const choose = (inventoryId: string) => {
		setChosen(inventoryId)
		setAnswer(null)
		setScanRound((n) => n + 1)
	}

	const current = open?.find((i) => i.inventoryId === chosen) ?? null
	const progress = chosen ? counts[chosen] : undefined
	const many = (open?.length ?? 0) > 1

	return (
		<div data-testid='inventory-scan'>
			<PageFrame
				heading={
					<>
						<span>{t('nav.inventory')}</span>
						{current && !many && <Badge variant='secondary'>{current.partnerName}</Badge>}
					</>
				}
				actions={
					open &&
					many && (
						<div className='flex items-center gap-2' data-testid='inventory-scan-choose'>
							<span className='text-sm text-muted-foreground'>{t('inventoryScan.choose')}</span>
							{open.map((i) => (
								<Button
									key={i.inventoryId}
									size='sm'
									variant={i.inventoryId === chosen ? 'default' : 'outline'}
									onClick={() => choose(i.inventoryId)}
									data-testid={`inventory-scan-partner-${i.source}`}
								>
									{i.partnerName}
								</Button>
							))}
						</div>
					)
				}
			>
				{!open ? (
					<p className='text-sm text-muted-foreground'>{error ?? t('inventoryScan.loading')}</p>
				) : open.length === 0 ? (
					<p className='text-sm text-muted-foreground' data-testid='inventory-scan-none'>
						{t('inventoryScan.none')}
					</p>
				) : !chosen ? (
					<p className='text-sm text-muted-foreground'>{t('inventoryScan.chooseFirst')}</p>
				) : (
					<div className='flex flex-col gap-8'>
						{progress && (
							<Stats testId='inventory-scan-progress'>
								<Stat
									label={t('inventoryScan.statTaken')}
									value={t('inventoryScan.ofTotal', { taken: String(progress.taken), total: String(progress.total) })}
									valueTestId='inventory-scan-taken'
								/>
								<Stat label={t('inventoryScan.statPending')} value={progress.pending} valueTestId='inventory-scan-pending' />
								<Stat
									label={t('inventoryScan.statExtras')}
									value={progress.extras}
									tone='warn'
									valueTestId='inventory-scan-extras'
								/>
							</Stats>
						)}
						<section className='flex flex-col gap-3'>
							<ScanInput key={scanRound} onScan={onScan} disabled={busy} />
							{error && (
								<p role='alert' className='text-sm text-destructive' data-testid='inventory-scan-error'>
									{error}
								</p>
							)}
							{answer ? (
								<Answer answer={answer} />
							) : (
								<p className='text-xs text-muted-foreground'>{t('inventoryScan.hint')}</p>
							)}
						</section>
						<ScannedTable rows={scanned[chosen] ?? []} />
					</div>
				)}
			</PageFrame>
		</div>
	)
}

/** The headline and tone of one answer, by `outcome`. */
function useAnswerWords() {
	const { t } = useTranslate()
	return (answer: InventoryScanAnswer): { title: string; tone: Tone } => {
		if (answer.outcome === 'refused') return { title: t(`inventoryRefusal.${answer.reason}`), tone: 'refused' }
		const title =
			answer.outcome === 'taken'
				? t('inventoryScan.taken')
				: answer.outcome === 'already_taken'
					? t('inventoryScan.alreadyTaken')
					: answer.outcome === 'already_tezgo'
						? t('inventoryScan.alreadyTezgo')
						: answer.repeated
							? t('inventoryScan.notOnListRepeated')
							: t('inventoryScan.notOnList')
		return { title, tone: answer.tone }
	}
}

function Answer({ answer }: { answer: InventoryScanAnswer }) {
	const { t } = useTranslate()
	const { title, tone } = useAnswerWords()(answer)
	const detail =
		answer.outcome === 'refused'
			? undefined
			: answer.recipientName !== null && answer.amountOwed !== null
				? [
						answer.trackingCode,
						answer.recipientName || '—',
						t('inventoryScan.som', { amount: amount(answer.amountOwed) }),
						answer.paid ? t('inventoryScan.paid') : '',
					]
						.filter(Boolean)
						.join(' · ')
				: answer.trackingCode
	return (
		<ToneBanner
			tone={tone}
			title={title}
			detail={detail}
			testId='inventory-scan-answer'
			titleTestId='inventory-scan-answer-title'
			detailTestId='inventory-scan-answer-detail'
			attributes={{ 'data-outcome': answer.outcome }}
		/>
	)
}

const TONE_TEXT: Record<Tone, string> = {
	ready: 'text-emerald-700 dark:text-emerald-400',
	attention: 'text-amber-700 dark:text-amber-400',
	refused: 'text-destructive',
}

/** What was scanned on this visit, newest first, each with its answer. */
function ScannedTable({ rows }: { rows: Scanned[] }) {
	const { t } = useTranslate()
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('inventoryScan.scannedTitle')} count={rows.length} />
			<CompactTable testId='inventory-scan-scanned'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('inventoryScan.colCode')}</TableHead>
						<TableHead>{t('inventoryScan.colRecipient')}</TableHead>
						<TableHead className='text-right'>{t('inventoryScan.colAmount')}</TableHead>
						<TableHead>{t('inventoryScan.colAnswer')}</TableHead>
					</TableRow>
				</TableHeader>
				{rows.length === 0 ? (
					<EmptyTableBody columns={4}>{t('inventoryScan.scannedEmpty')}</EmptyTableBody>
				) : (
					<TableBody>
						{rows.map((s) => {
							const a = s.answer.outcome === 'refused' ? null : s.answer
							return (
								<TableRow key={s.id} data-outcome={s.answer.outcome}>
									<TableCell className='font-mono'>{a?.trackingCode ?? s.code}</TableCell>
									<TableCell>{a?.recipientName || '—'}</TableCell>
									<TableCell className='text-right tabular-nums'>
										{a?.amountOwed != null ? (
											<>
												{amount(a.amountOwed)}
												{a.paid && (
													<span className='ml-2 text-xs text-muted-foreground'>{t('inventoryScan.paid')}</span>
												)}
											</>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell className={cn('font-medium', TONE_TEXT[s.tone])}>{s.title}</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}
